import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperienceDetailsModalAboutSection from 'components/Experiences/ExperienceDetailsModal/About/ExpereinceDetailsModalAboutSection'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import { getDefaultTicketModes } from 'lib/experiences/ticketModeUtils'
import { EXPERIENCE_ANY_DATE } from 'constants/experience'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import MarkdownRender from 'LuxKit/MarkdownRender'
import LOUNGE_AIRPORT_LOOKUP from './loungeData/loungeAirportLookup'

function LoungePassPackagesList({ ticketNames }: { ticketNames: Array<string> }) {
  const intl = useIntl()
  return <VerticalSpacer gap={12}>
    <BodyTextBlock variant="medium" weight="bold">
      {intl.formatMessage({
        id: '9kHHFCxLtW94MmeLKtFE',
        defaultMessage: 'Available packages',
        description: 'Section heading for available lounge package options in the lounge pass info modal',
      })}
    </BodyTextBlock>
    <MarkdownRender type="compact" content={ticketNames.map(name => `- ${name}`).join('\n')} />
  </VerticalSpacer>
}

interface Props {
  experience: App.ExperienceOffer
}

function LoungePassInfoModal({ experience }: Props) {
  const { resolve } = useModalElementContext()
  const intl = useIntl()
  const experienceView = useMemo(() => mapExperienceToBookingView(experience), [experience])

  const defaultTicketModesMap = useMemo(
    () => getDefaultTicketModes(experience.ticketModes ?? []),
    [experience.ticketModes],
  )

  const redemptionLocationId = experience.redemptionLocations[0]?.id

  const [timeslots] = useExperienceAvailableTimes(
    experience.provider !== 'asp' ? experience : undefined,
    {
      date: EXPERIENCE_ANY_DATE,
      redemptionLocationId,
      ticketModesMap: defaultTicketModesMap,
    },
  )

  const ticketNames = useMemo(() => {
    const tickets = timeslots[0]?.tickets ?? []
    return [...new Set(tickets.filter(t => t.name).map(t => t.name))]
  }, [timeslots])

  const airportInfo = LOUNGE_AIRPORT_LOOKUP[experience.id]
  const title = airportInfo
    ? `${airportInfo.name} (${airportInfo.code})`
    : (experience.location.city ?? experience.location.name ?? experience.name)

  return <ModalBase>
    <ModalHeader
      title={title}
      subtitle={experience.name}
      onCloseButtonClick={_e => resolve()}
    />
    <ModalBody>
      {experience.images.length > 0 && <ImageGallery
        images={experience.images}
        imageParams={{ mobileAspectRatio: '16:9', tabletAspectRatio: '16:9', quality: 'good', fit: 'none' }}
        size="small"
        showImageTitles={false}
        disableLightbox
      />}
      <ModalContent>
        <ExperienceDetailsModalAboutSection
          experienceView={experienceView}
          variant="overview-first"
          title={intl.formatMessage({
            id: 'nPu8sq6goMi-SkMWrehQ',
            defaultMessage: 'About the Lounge Pass',
            description: 'Title for the about section in the lounge pass info modal',
          })}
          descriptionMaxHeight={120}
          afterDescription={ticketNames.length > 0
            ? <LoungePassPackagesList ticketNames={ticketNames} />
            : undefined}
        />
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default LoungePassInfoModal
